import React, { Component } from 'react';
import { observer, Provider } from 'mobx-react';

import UserStore from 'js/stores/pages/AdminDashboardPage/UserStore';
import { phoneFormatter } from 'js/services/Format';
import { adminPanelColor } from 'js/config/style';

import Modal from 'js/components/core/Modal';
import SubmitRow from 'js/components/core/Modal/SubmitRow';
import Button from 'js/components/common/Button';

import LoadingSpinner from '../../LoadingSpinner';
import DashPanel from '../../DashPanel';
import Header from '../../DashPanel/Header';
import Content from '../../DashPanel/Content';
import DropMenu from '../../DashPanel/DropMenu';

import Link from './Link';
import Icon from './Icon';
import Title from './Title';
import Text from './Text';

import Card from './Card';
import Display from './Display';
import Image from './Image';
import Details from './Details';
import Label from './Label';
import Status from './Status';
import LoginAs from './LoginAs';
import Balance from './Balance';
import Tools from './Tools';

import List from '../../List';
import Row from '../../List/Row';
import ListItem from '../../List/ListItem';
import ListMessage from '../../List/ListMessage';

import CreditCard from './CreditCard';
import BankAccount from './BankAccount';
import Item from './Item';
import Token from './Token';
import Purchase from './Purchase';
import Sale from './Sale';
import Transaction from './Transaction';
import Question from './Question';

@observer
export default class User extends Component {
  user = UserStore.create({ email: this.props['*'] });

  constructor(props) {
    super(props);
    this.user.load();
  }

  render() {
    const user = this.user;
    return (
      <>
        <DashPanel>
          <Header>
            <DropMenu />
            <Title>
              <Link to="/admin/users">Users</Link>
              {user.email && (
                <>
                  <Icon name="chevron-right" />
                  <Text>{user.email}</Text>
                </>
              )}
            </Title>
          </Header>

          <Provider model={user}>
            <Content background={adminPanelColor}>
              {this.user.isLoading ? (
                <LoadingSpinner />
              ) : (
                <>
                  <Card>
                    <Tools>
                      <Status onClick={user.promptStatusToggle} />
                      {user.is_active && <LoginAs onClick={user.promptLogin} />}
                    </Tools>

                    <Display>
                      <Image />
                    </Display>

                    <Details>
                      <Label>Created</Label>
                      <div>{user.created_at}</div>

                      <Label>Name</Label>
                      <div>{user.name || '-'}</div>

                      <Label>Display name</Label>
                      <div>@{user.login}</div>

                      <Label>Email address</Label>
                      <div>
                        <a target="_blank" href={`mailto:${user.email}`}>
                          {user.email}
                        </a>
                      </div>

                      <Label>Phone number</Label>
                      <div>
                        {user.phone_number ? (
                          <a href={`tel:${user.phone_number}`}>{phoneFormatter(user.phone_number)}</a>
                        ) : (
                          '-'
                        )}
                      </div>

                      <Label>Address</Label>
                      <div>{user.address ? user.address : '-'}</div>

                      <Label>Balance</Label>
                      <div>
                        <Balance balance={user.balance} />
                      </div>
                    </Details>
                  </Card>

                  <div>
                    <Row cols={2}>
                      <List paginator={user.items} title="Items" icon="archive" controls="pages">
                        <div>
                          {user.items.items.length ? (
                            user.items.items.map(item => (
                              <ListItem key={item.alias} to={`/admin/items/item/${item.alias}`}>
                                <Item item={item} />
                              </ListItem>
                            ))
                          ) : (
                            <ListMessage>No items found</ListMessage>
                          )}
                        </div>
                      </List>

                      <List paginator={user.tokens} title="Tokens" icon="tag" controls="pages">
                        <div>
                          {user.tokens.tokens.length ? (
                            user.tokens.tokens.map(token => (
                              <ListItem key={token.alias} to={`/admin/tokens/token/${token.alias}`}>
                                <Token token={token} />
                              </ListItem>
                            ))
                          ) : (
                            <ListMessage>No tokens found</ListMessage>
                          )}
                        </div>
                      </List>
                    </Row>

                    <Row cols={2}>
                      <List paginator={user.purchases} title="Purchases" icon="money" controls="pages">
                        <div>
                          {user.purchases.purchases.length ? (
                            user.purchases.purchases.map(purchase => (
                              <ListItem key={purchase.uid} to={`/admin/orders/order/${purchase.uid}`}>
                                <Purchase purchase={purchase} />
                              </ListItem>
                            ))
                          ) : (
                            <ListMessage>No purchases found</ListMessage>
                          )}
                        </div>
                      </List>

                      <List paginator={user.sales} title="Sales" icon="money" controls="pages">
                        <div>
                          {user.sales.sales.length ? (
                            user.sales.sales.map(sale => (
                              <ListItem key={sale.uid} to={`/admin/orders/order/${sale.uid}`}>
                                <Sale sale={sale} />
                              </ListItem>
                            ))
                          ) : (
                            <ListMessage>No sales found</ListMessage>
                          )}
                        </div>
                      </List>
                    </Row>

                    <Row cols={2}>
                      <List paginator={user.credit_cards} title="Credit cards" icon="credit-card" controls="pages">
                        <div>
                          {user.credit_cards.credit_cards.length ? (
                            user.credit_cards.credit_cards.map(credit_card => (
                              <ListItem key={credit_card.id} to={`/admin/credit_cards/credit_card/${credit_card.id}`}>
                                <CreditCard credit_card={credit_card} />
                              </ListItem>
                            ))
                          ) : (
                            <ListMessage>No credit cards found</ListMessage>
                          )}
                        </div>
                      </List>

                      <List paginator={user.bank_accounts} title="Bank accounts" icon="institution" controls="pages">
                        <div>
                          {user.bank_accounts.bank_accounts.length ? (
                            user.bank_accounts.bank_accounts.map(bank_account => (
                              <ListItem
                                key={bank_account.id}
                                to={`/admin/bank_accounts/bank_account/${bank_account.id}`}>
                                <BankAccount bank_account={bank_account} />
                              </ListItem>
                            ))
                          ) : (
                            <ListMessage>No bank accounts found</ListMessage>
                          )}
                        </div>
                      </List>
                    </Row>

                    <Row>
                      <List paginator={user.transactions} title="Transactions" icon="list" controls="more">
                        <div>
                          {user.transactions.transactions.length ? (
                            user.transactions.transactions.map(transaction => (
                              <ListItem key={transaction.id} to={`/admin/transactions/transaction/${transaction.id}`}>
                                <Transaction transaction={transaction} />
                              </ListItem>
                            ))
                          ) : (
                            <ListMessage>No transactions found</ListMessage>
                          )}
                        </div>
                      </List>
                    </Row>
                  </div>
                </>
              )}
            </Content>
          </Provider>
        </DashPanel>

        <Modal showModal={user.showTogglePrompt} onCancel={user.closeTogglePrompt} padded withCloseButton>
          <Question>
            {user.wasActive
              ? 'Are you sure you want to disable this user?'
              : 'Are you sure you want to activate this user?'}
          </Question>
          <SubmitRow>
            <Button onClick={user.toggleStatus} isLoading={user.isToggling}>
              {user.wasActive
                ? user.isToggling
                  ? 'Disabling user'
                  : 'Disable now'
                : user.isToggling
                ? 'Activating user'
                : 'Activate now'}
            </Button>
          </SubmitRow>
        </Modal>

        <Modal showModal={user.showLoginPrompt} onCancel={user.closeLoginPrompt} padded withCloseButton>
          <Question>Are you sure you want to login as this user?</Question>
          <SubmitRow>
            <Button onClick={user.loginAs} isLoading={user.isLoggingInAs}>
              {user.isLoggingInAs ? 'Logging in' : 'Login now'}
            </Button>
          </SubmitRow>
        </Modal>
      </>
    );
  }
}
